"use client";

import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button, Description, Input, Label, Modal, Radio, RadioGroup, Spinner, TextField, toast } from "@heroui/react";
import { TreeSelect, type TreeSelectNode } from "@/components/custom";
import { buildTree, type TreeNode } from "@hs/shared";
import { trpc } from "@/lib/trpc";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const orgSchema = z.object({
  shortName: z.string().min(1, "组织简称不能为空").max(50, "组织简称不能超过50个字符"),
  code: z
    .string()
    .min(1, "组织代码不能为空")
    .max(50, "组织代码不能超过50个字符")
    .regex(/^[A-Za-z0-9_-]+$/, "组织代码只能包含字母、数字、下划线和连字符"),
  type: z.enum(["TEAM", "DEPT", "BRANCH"] as [string, ...string[]]),
  parentId: z.string().nullable().optional(),
});

type OrgFormValues = z.infer<typeof orgSchema>;

export type OrgEditorMode = "create" | "edit" | "add-child";

export interface OrgEditorDialogProps {
  open: boolean;
  mode?: OrgEditorMode;
  orgData?: Record<string, unknown>;
  parentOrg?: Record<string, unknown> | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function orgRecordsToTreeSelectNodes(orgs: Record<string, unknown>[]): TreeSelectNode[] {
  return orgs.map((org) => ({
    id: String(org.id),
    label: String(org.shortName || org.fullName || ""),
    children: org.children
      ? orgRecordsToTreeSelectNodes(org.children as Record<string, unknown>[])
      : undefined,
  }));
}

export function OrgEditorDialog({
  open,
  mode = "create",
  orgData,
  parentOrg,
  onOpenChange,
  onSuccess,
}: OrgEditorDialogProps) {
  const utils = trpc.useUtils();
  const isEdit = mode === "edit";

  const { data: orgTreeData, isLoading: isLoadingOrgTree } = trpc.contacts.findAllSpaceTrees.useQuery(
    undefined,
    { enabled: open && mode !== "edit" }
  );

  const parentOptions: TreeSelectNode[] = React.useMemo(() => {
    if (!orgTreeData?.length) return [];
    const flatForTree: TreeNode[] = orgTreeData.map((org) => ({
      id: String(org.id),
      parentId: org.parentId ?? null,
      shortName: org.shortName,
      fullName: org.fullName,
      order: org.order ?? 0,
    }));
    const tree = buildTree(flatForTree, { parentField: "parentId", sortBy: "order", sortOrder: "asc" });
    return orgRecordsToTreeSelectNodes(tree as Record<string, unknown>[]);
  }, [orgTreeData]);

  const form = useForm<OrgFormValues>({
    resolver: zodResolver(orgSchema),
    defaultValues: {
      shortName: "",
      code: "",
      type: "DEPT",
      parentId: null,
    },
  });

  const createOrgMutation = trpc.contacts.createOrg.useMutation({
    onSuccess: () => {
      toast.success("组织创建成功");
      form.reset();
      onOpenChange(false);
      onSuccess();
      void utils.contacts.findAllSpaceTrees.invalidate();
    },
  });

  const updateOrgMutation = trpc.contacts.updateOrg.useMutation({
    onSuccess: () => {
      toast.success("组织更新成功");
      form.reset();
      onOpenChange(false);
      onSuccess();
      void utils.contacts.findAllSpaceTrees.invalidate();
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      return;
    }
    if (isEdit && orgData) {
      form.reset({
        shortName: (orgData.shortName as string) || "",
        code: (orgData.code as string) || "",
        type: ((orgData.type as string) || "DEPT") as "TEAM" | "DEPT" | "BRANCH",
        parentId: (orgData.parentId as string | null) || null,
      });
    } else if (mode === "add-child" && parentOrg) {
      form.reset({
        shortName: "",
        code: "",
        type: "DEPT",
        parentId: parentOrg.id as string,
      });
    } else {
      form.reset({
        shortName: "",
        code: "",
        type: "DEPT",
        parentId: null,
      });
    }
  }, [open, mode, orgData, parentOrg, isEdit, form]);

  const dialogTitle =
    mode === "edit" ? "编辑组织" : mode === "add-child" ? "添加子组织" : "新建组织";
  const submitButtonText = mode === "edit" ? "保存" : mode === "add-child" ? "添加" : "确定";

  const handleSubmit = async (values: OrgFormValues) => {
    if (isEdit && orgData) {
      await updateOrgMutation.mutateAsync({
        id: orgData.id as string,
        data: {
          shortName: values.shortName,
          code: values.code,
          type: values.type,
        },
      });
    } else {
      await createOrgMutation.mutateAsync({
        shortName: values.shortName,
        code: values.code,
        type: values.type,
        parentId: values.parentId || null,
      });
    }
  };

  const isSubmitting = createOrgMutation.isPending || updateOrgMutation.isPending;
  const errors = form.formState.errors;

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) form.reset();
      onOpenChange(next);
    },
    [form, onOpenChange]
  );

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog className="max-w-md">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{dialogTitle}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="gap-4 p-1">
              <form id="org-editor-form" className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
                <TextField className="w-full" isDisabled={isSubmitting} isInvalid={!!errors.shortName}>
                  <Label className="text-sm font-medium">
                    组织简称 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="shortName"
                    render={({ field }) => (
                      <Input
                        maxLength={50}
                        placeholder="请输入组织简称"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.shortName ? (
                    <Description className="text-danger text-sm">{errors.shortName.message}</Description>
                  ) : null}
                </TextField>

                <TextField className="w-full" isDisabled={isSubmitting} isInvalid={!!errors.code}>
                  <Label className="text-sm font-medium">
                    组织代码 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <Input
                        maxLength={50}
                        placeholder="请输入组织代码"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.code ? (
                    <Description className="text-danger text-sm">{errors.code.message}</Description>
                  ) : null}
                </TextField>

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">
                    组织类型 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <RadioGroup
                        isDisabled={isSubmitting}
                        name={field.name}
                        orientation="horizontal"
                        ref={field.ref}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      >
                        <Radio value="DEPT">
                          <Radio.Control>
                            <Radio.Indicator />
                          </Radio.Control>
                          <Radio.Content>
                            <Label>部门</Label>
                          </Radio.Content>
                        </Radio>
                        <Radio value="BRANCH">
                          <Radio.Control>
                            <Radio.Indicator />
                          </Radio.Control>
                          <Radio.Content>
                            <Label>机构</Label>
                          </Radio.Content>
                        </Radio>
                        <Radio value="TEAM">
                          <Radio.Control>
                            <Radio.Indicator />
                          </Radio.Control>
                          <Radio.Content>
                            <Label>团队</Label>
                          </Radio.Content>
                        </Radio>
                      </RadioGroup>
                    )}
                  />
                  {errors.type ? (
                    <Description className="text-danger text-sm">{errors.type.message}</Description>
                  ) : null}
                </div>

                {mode !== "edit" ? (
                  <TextField
                    className="w-full"
                    isDisabled={isSubmitting || mode === "add-child"}
                    isInvalid={!!errors.parentId}
                  >
                    <Label className="text-sm font-medium">上级组织</Label>
                    <Controller
                      control={form.control}
                      name="parentId"
                      render={({ field }) => (
                        <TreeSelect
                          aria-label="选择上级组织"
                          disabled={isSubmitting || mode === "add-child"}
                          emptyMessage="暂无组织数据"
                          isLoading={isLoadingOrgTree}
                          items={parentOptions}
                          placeholder="选择上级组织"
                          selectionMode="single"
                          value={field.value ?? null}
                          onChange={(v) => {
                            const id = typeof v === "string" ? v : Array.isArray(v) ? v[0] ?? null : null;
                            field.onChange(id);
                          }}
                        />
                      )}
                    />
                    {errors.parentId ? (
                      <Description className="text-danger text-sm">
                        {String(errors.parentId.message)}
                      </Description>
                    ) : null}
                  </TextField>
                ) : null}
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" isDisabled={isSubmitting} onPress={() => handleModalOpenChange(false)}>
                取消
              </Button>
              <Button
                form="org-editor-form"
                isDisabled={isSubmitting}
                type="submit"
                variant="primary"
              >
                {isSubmitting ? "提交中…" : submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
